import { useEffect, useState } from 'react'
import { Box, Typography, CircularProgress, Card, CardActionArea } from '@mui/material'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import { fetchCertificateTemplates } from '../medicalCertificateService'
import { CERTIFICATE_CATEGORY, CATEGORY_LABELS } from '../certificateUtils'

/**
 * Step 1 of the create flow: shows the role/discipline-filtered templates the practitioner may issue,
 * split into Medical Certificates and Consent Forms (mirrors EkaCare's two-column chooser).
 */
export default function CertificateTemplateChooser({ clinicId, discipline, onSelect }) {
  const [loading, setLoading] = useState(true)
  const [templates, setTemplates] = useState([])
  const [error, setError] = useState(null)

  useEffect(() => {
    let active = true
    setLoading(true)
    fetchCertificateTemplates({ clinicId, discipline })
      .then((res) => {
        if (!active) return
        if (res.ok) {
          setTemplates(res.templates)
        } else {
          setError(res.message)
        }
      })
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [clinicId, discipline])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    )
  }

  if (!templates.length) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No medical document templates are available for your role yet.
        </Typography>
      </Box>
    )
  }

  const certificates = templates.filter((t) => t.category !== CERTIFICATE_CATEGORY.CONSENT)
  const consents = templates.filter((t) => t.category === CERTIFICATE_CATEGORY.CONSENT)

  return (
    <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      <TemplateColumn
        title={`Select a ${CATEGORY_LABELS[CERTIFICATE_CATEGORY.CERTIFICATE].toLowerCase()} template`}
        templates={certificates}
        onSelect={onSelect}
      />
      {consents.length > 0 && (
        <TemplateColumn
          title={`Select a ${CATEGORY_LABELS[CERTIFICATE_CATEGORY.CONSENT].toLowerCase()} template`}
          templates={consents}
          onSelect={onSelect}
        />
      )}
    </Box>
  )
}

function TemplateColumn({ title, templates, onSelect }) {
  if (!templates.length) return null
  return (
    <Box sx={{ flex: 1, minWidth: 280 }}>
      <Typography sx={{ fontSize: 15, fontWeight: 600, mb: 1.5 }}>{title}</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 1.5 }}>
        {templates.map((template) => (
          <Card
            key={template.id}
            variant="outlined"
            sx={{ borderRadius: 2, '&:hover': { borderColor: 'primary.main', boxShadow: 1 } }}
          >
            <CardActionArea
              onClick={() => onSelect(template)}
              sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75, textAlign: 'center' }}
            >
              <DescriptionOutlinedIcon sx={{ color: 'primary.main' }} />
              <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.25 }}>{template.title}</Typography>
              {template.description && (
                <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.2 }}>
                  {template.description}
                </Typography>
              )}
            </CardActionArea>
          </Card>
        ))}
      </Box>
    </Box>
  )
}
